import React, { useCallback, useEffect, useState } from 'react';
import uniqid from 'uniqid';
import {
  Box,
  Divider,
  Heading,
  HStack,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Wrap,
} from '@chakra-ui/react';
import {
  getChildrenAges,
  getNumberRanges,
  getBookingParamsTravelFormValidationSchema,
  getTravelFormBookingParams,
  getMaxNumberOfChildren,
} from '../../services/Booking/BookingService';
import { Button } from '@chakra-ui/button';
import { ObjectParams } from '../../types/ParamsTypes';
import { SingleObject } from '../../types/ObjectTypes';
import { useFormik } from 'formik';
import { FormikSelect } from '../common/Form/FormikSelect';
import { BookingServiceForm } from './BookingServiceForm';
import { BookingParams } from '../../types/BookingTypes';
import {
  getExtendedObjectRoute,
  getObjectParamsFromBookingParams,
} from '../../services/Object/ObjectService';
import { getErrors } from '../../services/Joi/JoiValidationService';
import { Flex } from '@chakra-ui/layout';
import { ExtendedDetailsRoute } from '../../types/RoutesTypes';
import { useHistory, useParams } from 'react-router-dom';
import { DataOptions, getDataOptions } from '../../services/DataOption/DataOptionsService';

export interface BookingTravelDataFormProps {
  object: SingleObject;
  bookingParams: BookingParams;
  handleObjectParamsChange: (objectParams: ObjectParams, bookingParams: BookingParams) => void;
  handleStepChange: (value: number) => void;
  currentStep: number;
}

export const BookingTravelDataForm = (props: BookingTravelDataFormProps): JSX.Element => {
  const { object, bookingParams, handleObjectParamsChange, handleStepChange, currentStep } = props;
  const routeParams: ExtendedDetailsRoute = useParams();
  const history = useHistory();
  const [dataOptions] = useState<DataOptions>(getDataOptions());

  const formik = useFormik({
    initialValues: bookingParams,
    onSubmit: () => {
      handleStepChange(currentStep + 1);
    },
    validate: (values: BookingParams) => {
      const errors = getErrors(
        getBookingParamsTravelFormValidationSchema(object),
        getTravelFormBookingParams(values),
        { baseData: object.baseData },
      );

      if (Object.keys(errors).length === 0) {
        const convertedObjectParams = getObjectParamsFromBookingParams(values);
        handleObjectParamsChange(convertedObjectParams, values);
      }

      return errors;
    },
  });

  const formValues = formik.values;

  const handleNumberOfChildrenChange = useCallback(
    (name, value) => {
      const agesOfChildren = Array(value).fill(0);

      setTimeout(() => {
        formik.setFieldValue(name, value);
        formik.setFieldValue('agesOfChildren', agesOfChildren);
      });
    },
    [formik.values.numberOfChildren, formik.values.agesOfChildren],
  );

  useEffect(() => {
    const scrollTarget = document.getElementById('ihreReisedaten') as HTMLElement;
    if(scrollTarget != null){
        scrollTarget.scrollIntoView({ behavior: "smooth" });
    }
  }, []);
  
  const handleRedirectToBooking = useCallback(() => {
    const objectParams = getObjectParamsFromBookingParams(formik.values);
    const objectRoute = getExtendedObjectRoute(object, objectParams);
    history.push(objectRoute);
  }, [object, routeParams, formik.values]);

  return (
    <VStack
      p={4}
      borderRadius={{ sm: 0, md: 4, lg: 4, xl: 4 }}
      borderWidth={1}
      borderColor={'gray.300'}
      backgroundColor={'white'}
      minW="100%"
      gridGap={4}
      id={'ihreReisedaten'}>
      <Text w={'100%'} textAlign={'left'} fontSize={'xl'} textStyle={'category.primary'}>
        Ihre Reisedaten
      </Text>

      <VStack
        alignItems={'flex-start'}
        width={'100%'}
        gridGap={4}
        as={'form'}
        // @ts-ignore
        onSubmit={formik.handleSubmit}>
        <Divider />

        <Heading size={'md'} fontWeight={'semibold'} flex={1}>
          Personen
        </Heading>

        <VStack width={'100%'} alignSelf={'flex-end'}>
          <Flex
            alignItems={'flex-start'}
            width={'100%'}
            gridGap={4}
            flexDirection={{ base: 'column', md: 'row' }}>
            <Flex flex={1} w={{ base: '100%', md: 'unset' }}>
              <FormikSelect
                formik={formik}
                label={'Erwachsene'}
                name={'numberOfAdults'}
                valueType={'number'}
                options={getNumberRanges(1, object.baseData.maximumPersons)}
              />
            </Flex>

            { !dataOptions.disableChildren &&
              <Flex flex={1} flexDirection={'column'} w={{ base: '100%', md: 'unset' }}>
                <FormikSelect
                  formik={formik}
                  label={'Kinder'}
                  onChange={handleNumberOfChildrenChange}
                  name={'numberOfChildren'}
                  valueType={'number'}
                  options={getNumberRanges(0, getMaxNumberOfChildren(object, bookingParams))}
                />
                <VStack width={'100%'} pl={8}>
                  {formValues.agesOfChildren &&
                    formValues.agesOfChildren.map((age, idx) => {
                      return (
                        <FormikSelect
                          key={uniqid()}
                          formik={formik}
                          idx={idx}
                          label={`Kind ${idx + 1}`}
                          name={'agesOfChildren'}
                          valueType={'number'}
                          options={getChildrenAges()}
                        />
                      );
                    })}
                </VStack>
              </Flex>
            }
          </Flex>
        </VStack>
        {(object.baseData.numberOfPets > 0 && !dataOptions.disablePets) && (
          <>
            <Heading size={'md'} fontWeight={'semibold'} flex={1}>
              Haustiere
            </Heading>
            <HStack alignItems={'flex-start'} width={'100%'}>
              <Spacer />
              <Flex width={320}>
                <FormikSelect
                  label={'Haustiere'}
                  name={'numberOfPets'}
                  formik={formik}
                  options={getNumberRanges(0, object.baseData.numberOfPets)}
                />
              </Flex>
            </HStack>
          </>
        )}
        <Divider />

        <VStack alignItems={'flex-start'} width={'100%'} gridGap={4}>
          <Heading size={'md'} fontWeight={'semibold'} flex={1}>
            Zusatzleistungen
          </Heading>
          <Box
            overflow={'auto'}
            w={'100%'}
            css={{
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#bfbfbf',
                borderRadius: '24px',
              },
            }}>
            <Table size={'lg'} width={'100%'}>
              <Thead>
                <Tr>
                  <Th p={0} pr={4}>
                    Beschreibung / Anzahl
                  </Th>
                  <Th />
                  <Th />
                  <Th p={0} textAlign={'right'}>
                    Einzelpreis
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {object.servicesData.filter(serviceData => !serviceData.Mandatory).length > 0 &&
                  object.servicesData
                    .filter(serviceData => !serviceData.Mandatory)
                    .map((service, idx) => {
                      return (
                        <BookingServiceForm
                          key={uniqid()}
                          idx={idx}
                          formik={formik}
                          name={'services'}
                          objectService={service}
                        />
                      );
                    })}
                {object.servicesData.filter(serviceData => !serviceData.Mandatory).length === 0 && (
                  <Tr>
                    <Td colSpan={4}>
                      <Text textAlign={'center'}>Keine Zusatzleistungen verfügbar.</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </VStack>
        <HStack width={'100%'}>
          <Button
            alignSelf={'flex-start'}
            variant={'ghost'}
            colorScheme={'blue'}
            onClick={handleRedirectToBooking}>
            Zurück
          </Button>
          <Spacer />
          <Button
            isDisabled={!formik.isValid}
            alignSelf={'flex-end'}
            colorScheme={'blue'}
            type={'submit'}>
            Nächster Schritt
          </Button>
        </HStack>
      </VStack>
    </VStack>
  );
};
